import React, { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useUserRegion } from '@frontend/components/agromarket/components/RegionSelector'
import { FundingProduct } from '@frontend/components/agromarket/core/access-control'
import { Steps } from '@/components/composed'
import { ConsultingActionButton } from '@/components/composed/finansingConsulting/ConsultingActionButton'
import { LandingAffiliation, LandingBenefits, LandingHeader, LandingLeasingOffer } from '@/components/composed/landingBlocks'
import { LandingAccessControlProvider } from '@/components/composed/landingBlocks/LandingAccessControlProvider'
import { LandingFinancingProductPopups } from '@/components/composed/landingBlocks/LandingFinancingProductPopups'
import { getLocation } from '@/logic/app/reducer'
import { financingProduct } from '@/logic/metrika/constants'
import {
  getEventContextGtmFinancingOfferForm,
  getGtmScrollEventPayloadFinancingOfferForm,
  pushGtmEventOnClickApplyOnFundingOfferPage,
  pushGtmEventOnShowLendingFinancingProducts,
} from '@/logic/metrika/financing/gtmHelpers'
import type { IBaseGtmEventFinancing } from '@/logic/metrika/financing/types'
import { getUserGtmInfo } from '@/logic/metrika/helpers'
import { usePushScrollGtmEvent } from '@/logic/metrika/usePushScrollGtmEvent'
import type { RootState } from '@/redux/interfaces'
import type { EFinancingOfferRoute } from '@/types'
import { EFinancingOfferType } from '@/types'
import { asyncScrollIntoView } from '@/utils/helpers'
import { useWebpExtension } from '@/utils/hooks/image'
import { useDeviceType } from '@/utils/hooks/useDeviceType'
import { getStaticImagePath } from '@/utils/images'
import { BANNER_BENEFITS, BENEFITS, DETAILS, PARTNER_IMAGES, ROOT_BREADCRUMBS, STEPS } from './constants'
import { EScrollEventBlockId } from './gtmConstants'
import { messages } from './messages'
import './styles.scss'

const mapState = (state: RootState) => ({
  location: getLocation()(state),
  userGtmInfo: getUserGtmInfo()(state),
})

const AlfaLeasing: React.FC = () => {
  const { location, userGtmInfo } = useSelector(mapState)
  const { userRegion } = useUserRegion()
  const imageExtension = useWebpExtension()

  const { isDesktop, isMobile } = useDeviceType()

  const leavingRequestClicked = () => () => {
    asyncScrollIntoView(`#${EScrollEventBlockId.financingOfferForm}`)
    pushGtmEventOnClickApplyOnFundingOfferPage(userGtmInfo, userRegion.name, FundingProduct.LEASING_ALFA)
  }

  useEffect(() => {
    pushGtmEventOnShowLendingFinancingProducts(
      userGtmInfo,
      getEventContextGtmFinancingOfferForm(location.pathname, financingProduct[location.pathname as EFinancingOfferRoute]),
    ) // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  usePushScrollGtmEvent<EScrollEventBlockId, IBaseGtmEventFinancing>({
    blockIds: [EScrollEventBlockId.financingOfferForm],
    gtmScrollEventPayloads: {
      [EScrollEventBlockId.financingOfferForm]: getGtmScrollEventPayloadFinancingOfferForm(
        userGtmInfo,
        userRegion.name,
        FundingProduct.LEASING_ALFA,
      ),
    },
  })

  const backgroundImage = getStaticImagePath(`financingOffer/alfaLeasing.${imageExtension}`)

  return (
    <LandingAccessControlProvider>
      <div className="alfa-leasing-page">
        <LandingHeader
          isDeviceMobile={isMobile}
          actionButtonText={messages.leaveRequest}
          titleText={messages.header.title}
          descriptionText={messages.header.description}
          styles={{
            backgroundImage: isDesktop ? `url("${backgroundImage}")` : undefined,
          }}
          partnerImages={PARTNER_IMAGES}
          onActionClick={leavingRequestClicked()}
          details={DETAILS}
          breadcrumbCaption={messages.breadcrumb}
          rootBreadcrumbs={ROOT_BREADCRUMBS}
          buttonTooltipMessage={messages.financingOffer.hint.forAgrariansOnly}
          additionalActions={<ConsultingActionButton />}
        />
        <LandingBenefits
          bannerTitleText={messages.benefits.banner.title}
          bannerBackgroundImage="financingOffer/agriculturalMachinesAndEquipmentLeasingBenefits"
          benefits={BENEFITS}
          bannerBenefits={BANNER_BENEFITS}
          onActionClick={leavingRequestClicked()}
          blockId={EScrollEventBlockId.alfaLeasingPageBenefits}
          shouldShowBannerBeforeTitle
          titleSizeXs={null}
          titleSize={2}
          buttonTooltipMessage={messages.financingOffer.hint.forAgrariansOnly}
        />
        <LandingAffiliation
          descriptionText={messages.affiliation.description}
          requirementsText={messages.affiliation.requirements.text}
          requirementsAlertText={messages.affiliation.requirements.alert}
          lawText={messages.affiliation.law}
          shouldShowDescription
          shouldShowRequirement
          blockId={EScrollEventBlockId.alfaLeasingPageAffiliation}
        />
        <Steps
          blockId={EScrollEventBlockId.alfaLeasingPageStages}
          title={messages.steps.title}
          onButtonClick={leavingRequestClicked()}
          steps={STEPS}
          buttonText={messages.steps.button}
          buttonTooltipMessage={messages.financingOffer.hint.forAgrariansOnly}
        />
        <LandingLeasingOffer
          formId={EScrollEventBlockId.financingOfferForm}
          offerType={EFinancingOfferType.LEASING_ALFA}
          productId={FundingProduct.LEASING_ALFA}
        />
        <LandingFinancingProductPopups />
      </div>
    </LandingAccessControlProvider>
  )
}

export default AlfaLeasing
